import { useState, useEffect, useRef } from 'react';
import { Input, Button, Spin, Select } from '@bancobarigui/barihaus';
import { message, Tag } from 'antd';
import { SaveOutlined, EditOutlined, CloseOutlined, SearchOutlined, EnvironmentOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';

import { IdentificacaoData, WizardMode } from '@/features/laudo/types/laudo-wizard.types';
import { InspectionRequest, InspectionCheckin } from '@/services/types/inspection.types';
import { inspectionService } from '@/services/inspection.service';
import { fetchAddressByCep, isValidCep } from '@/services/geocode.service';
import { ReviewFieldWrapper } from '../../ReviewFieldWrapper';
import { useReviewMode } from '../../index';
import { usePropertyTypeOptions } from '@/features/laudo/hooks/usePropertyTypeOptions';

interface IdentificacaoStepProps {
  data: IdentificacaoData;
  onUpdate: (data: Partial<IdentificacaoData>) => void;
  inspection: InspectionRequest;
  mode?: WizardMode;
}

export function IdentificacaoStep({ data, onUpdate, inspection, mode = 'create' }: IdentificacaoStepProps) {
  const isReviewMode = mode === 'review';
  const isCorrectionMode = mode === 'correction';
  const { reviewContext } = useReviewMode();
  const { options: tipoImovelOptions } = usePropertyTypeOptions();
  const firstItem = inspection.items?.[0];

  // Verifica se é devolução externa (status 21) - sem correções pendentes específicas
  const isExternalReturn = isCorrectionMode && (!reviewContext?.pendingCorrections || reviewContext.pendingCorrections.length === 0);

  // Campos da seção "Dados do Imóvel"
  const propertyDataFields = [
    'identificacao.tipoImovel',
    'identificacao.matriculaNumero',
    'identificacao.iptuNumero',
    'identificacao.cartorioNumero',
    'identificacao.areaTotal',
    'identificacao.areaConstruida',
    'identificacao.areaPrivativa',
  ];

  // Campos da seção "Localização do Imóvel"
  const addressFields = [
    'identificacao.enderecoCompleto.cep',
    'identificacao.enderecoCompleto.logradouro',
    'identificacao.enderecoCompleto.numero',
    'identificacao.enderecoCompleto.complemento',
    'identificacao.enderecoCompleto.bairro',
    'identificacao.enderecoCompleto.cidade',
    'identificacao.enderecoCompleto.estado',
  ];

  // Verifica se há correção pendente em algum campo de cada seção
  const hasPropertyDataCorrection = isExternalReturn || (isCorrectionMode && reviewContext?.pendingCorrections?.some(
    c => propertyDataFields.includes(c.fieldPath)
  ));
  const hasAddressCorrection = isExternalReturn || (isCorrectionMode && reviewContext?.pendingCorrections?.some(
    c => addressFields.includes(c.fieldPath)
  ));

  // Estados de edição
  const [isEditingPropertyData, setIsEditingPropertyData] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  // Estados para campos de endereço
  const [cep, setCep] = useState(firstItem?.cep || '');
  const [street, setStreet] = useState(firstItem?.street || '');
  const [number, setNumber] = useState(firstItem?.number || '');
  const [complement, setComplement] = useState(firstItem?.complement || '');
  const [district, setDistrict] = useState(firstItem?.district || '');
  const [city, setCity] = useState(firstItem?.city || '');
  const [state, setState] = useState(firstItem?.state || '');

  // Estados para dados do imóvel
  const [tipoImovel, setTipoImovel] = useState(data.tipoImovel || firstItem?.propertyTypeName || '');
  const [iptuNumber, setIptuNumber] = useState(firstItem?.iptuNumber || '');
  const [registrationNumber, setRegistrationNumber] = useState(firstItem?.registrationNumber || '');
  const [registryOfficeNumber, setRegistryOfficeNumber] = useState(firstItem?.registryOfficeNumber || '');
  const [landArea, setLandArea] = useState<string>(firstItem?.landArea ? firstItem.landArea.toString() : '');
  const [builtArea, setBuiltArea] = useState<string>(firstItem?.builtArea ? firstItem.builtArea.toString() : '');
  const [privateArea, setPrivateArea] = useState<string>(firstItem?.privateArea ? firstItem.privateArea.toString() : '');

  const [isSavingPropertyData, setIsSavingPropertyData] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [isLoadingCep, setIsLoadingCep] = useState(false);

  // Para armazenar valores originais após salvar
  const [savedItem, setSavedItem] = useState(firstItem);

  // Referência para o timer de debounce do CEP
  const cepDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Estado para controlar a URL do mapa
  const [mapUrl, setMapUrl] = useState('');

  const [checkinData, setCheckinData] = useState<InspectionCheckin | null>(null);
  const [isLoadingCheckin, setIsLoadingCheckin] = useState(false);

  // Atualiza o mapa sempre que o endereço mudar
  useEffect(() => {
    const newMapUrl = `https://www.google.com/maps?q=${encodeURIComponent(
      `${street || ''}, ${number || ''}, ${district || ''}, ${city || ''}/${state || ''}`
    )}&output=embed`;
    setMapUrl(newMapUrl);
  }, [street, number, district, city, state]);

  useEffect(() => {
    const fetchCheckinData = async () => {
      if (!firstItem?.id) return;

      setIsLoadingCheckin(true);
      try {
        const checkin = await inspectionService.getCheckinByItem(firstItem.id);
        setCheckinData(checkin);
      } catch {
        setCheckinData(null);
      } finally {
        setIsLoadingCheckin(false);
      }
    };

    fetchCheckinData();
  }, [firstItem?.id]);

  // Detecta mudanças nos campos de endereço
  const hasAddressChanges =
    cep !== (savedItem?.cep || '') ||
    street !== (savedItem?.street || '') ||
    number !== (savedItem?.number || '') ||
    complement !== (savedItem?.complement || '') ||
    district !== (savedItem?.district || '') ||
    city !== (savedItem?.city || '') ||
    state !== (savedItem?.state || '');

  // Detecta mudanças nos campos de dados do imóvel
  const hasPropertyDataChanges =
    tipoImovel !== (data.tipoImovel || firstItem?.propertyTypeName || '') ||
    iptuNumber !== (savedItem?.iptuNumber || '') ||
    registrationNumber !== (savedItem?.registrationNumber || '') ||
    registryOfficeNumber !== (savedItem?.registryOfficeNumber || '') ||
    landArea !== (savedItem?.landArea?.toString() || '') ||
    builtArea !== (savedItem?.builtArea?.toString() || '') ||
    privateArea !== (savedItem?.privateArea?.toString() || '');

  // Função para buscar endereço por CEP
  const handleCepSearch = async (cepValue: string) => {
    const cleanCep = cepValue.replace(/\D/g, '');

    if (!isValidCep(cleanCep)) {
      return;
    }

    setIsLoadingCep(true);

    try {
      const addressData = await fetchAddressByCep(cleanCep);

      if (addressData) {
        setStreet(addressData.street);
        setDistrict(addressData.district);
        setCity(addressData.city);
        setState(addressData.state.toUpperCase());
        message.success('Endereço encontrado!');
      } else {
        message.warning('CEP não encontrado. Preencha os campos manualmente.');
      }
    } catch {
      message.error('Erro ao buscar o endereço.');
    } finally {
      setIsLoadingCep(false);
    }
  };

  // Handler do CEP com debounce
  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCep(value);

    // Limpa o timer anterior
    if (cepDebounceTimer.current) {
      clearTimeout(cepDebounceTimer.current);
    }

    const cleanCep = value.replace(/\D/g, '');

    // Se o CEP está completo, busca após 800ms
    if (cleanCep.length === 8 && isEditingAddress) {
      cepDebounceTimer.current = setTimeout(() => {
        handleCepSearch(cleanCep);
      }, 800);
    }
  };

  const handleSavePropertyData = async () => {
    if (!firstItem?.id) {
      message.error('ID do item não encontrado');
      return;
    }

    setIsSavingPropertyData(true);

    try {
      const payload: Record<string, string | number> = {};

      if (iptuNumber) payload.iptuNumber = iptuNumber;
      if (registrationNumber) payload.registrationNumber = registrationNumber;
      if (registryOfficeNumber) payload.registryOfficeNumber = registryOfficeNumber;
      if (landArea) payload.landArea = parseFloat(landArea);
      if (builtArea) payload.builtArea = parseFloat(builtArea);
      if (privateArea) payload.privateArea = parseFloat(privateArea);

      const updatedItem = await inspectionService.updateItemPropertyData(firstItem.id, payload as any);

      // Atualiza o estado salvo com os novos valores
      setSavedItem(updatedItem);

      // Sincroniza o estado do wizard com TUDO que acabou de ser salvo. O PDF é montado a
      // partir do wizard (o objeto `inspection` desta tela não é recarregado após o save),
      // então campo que não for propagado aqui sai desatualizado no documento.
      onUpdate({
        tipoImovel,
        matriculaNumero: registrationNumber,
        matriculaCartorio: registryOfficeNumber,
        iptuNumero: iptuNumber,
        areaTotal: landArea ? parseFloat(landArea) : 0,
        areaConstruida: builtArea ? parseFloat(builtArea) : 0,
        areaPrivativa: privateArea ? parseFloat(privateArea) : 0,
      });

      message.success('Dados do imóvel salvos com sucesso!');
      setIsEditingPropertyData(false);
    } catch {
      message.error('Erro ao salvar os dados do imóvel');
    } finally {
      setIsSavingPropertyData(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!firstItem?.id) {
      message.error('ID do item não encontrado');
      return;
    }

    setIsSavingAddress(true);

    try {
      const payload: Record<string, string> = {
        cep,
        street,
        number,
        district,
        city,
        state,
      };

      if (complement) {
        payload.complement = complement;
      }

      const updatedItem = await inspectionService.updateItemAddress(firstItem.id, payload as any);

      // Atualiza o estado salvo com os novos valores
      setSavedItem(updatedItem);

      // Sincroniza com o estado do wizard para que outros steps usem o endereço atualizado
      const enderecoCompleto = `${street}, ${number}${complement ? `, ${complement}` : ''} - ${district}, ${city}/${state}`;
      onUpdate({
        enderecoCompleto,
        // Também salvar os campos individuais para uso no VistoriaStep
        cep,
        logradouro: street,
        numero: number,
        complemento: complement,
        bairro: district,
        cidade: city,
        estado: state,
      });

      message.success('Localização salva com sucesso!');
      setIsEditingAddress(false);
    } catch {
      message.error('Erro ao salvar a localização');
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleCancelPropertyData = () => {
    // Restaurar valores originais
    setTipoImovel(data.tipoImovel || firstItem?.propertyTypeName || '');
    setIptuNumber(savedItem?.iptuNumber || '');
    setRegistrationNumber(savedItem?.registrationNumber || '');
    setRegistryOfficeNumber(savedItem?.registryOfficeNumber || '');
    setLandArea(savedItem?.landArea ? savedItem.landArea.toString() : '');
    setBuiltArea(savedItem?.builtArea ? savedItem.builtArea.toString() : '');
    setPrivateArea(savedItem?.privateArea ? savedItem.privateArea.toString() : '');
    setIsEditingPropertyData(false);
  };

  const handleCancelAddress = () => {
    // Restaurar valores originais
    setCep(savedItem?.cep || '');
    setStreet(savedItem?.street || '');
    setNumber(savedItem?.number || '');
    setComplement(savedItem?.complement || '');
    setDistrict(savedItem?.district || '');
    setCity(savedItem?.city || '');
    setState(savedItem?.state || '');
    setIsEditingAddress(false);

    // Limpa o timer se houver
    if (cepDebounceTimer.current) {
      clearTimeout(cepDebounceTimer.current);
    }
  };

  // Cleanup do timer quando o componente desmonta
  useEffect(() => {
    return () => {
      if (cepDebounceTimer.current) {
        clearTimeout(cepDebounceTimer.current);
      }
    };
  }, []);

  return (
    <div className="identificacao-step">
      {checkinData && (
        <div style={{
          marginBottom: 16,
          padding: '10px 16px',
          border: '1px solid #e8e8e8',
          borderRadius: 6,
          backgroundColor: checkinData.isWithinAllowedRadius ? '#f6ffed' : '#fffbe6',
          borderColor: checkinData.isWithinAllowedRadius ? '#b7eb8f' : '#ffe58f'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <EnvironmentOutlined style={{ color: checkinData.isWithinAllowedRadius ? '#52c41a' : '#faad14' }} />
                <span style={{ fontWeight: 500, fontSize: 13 }}>Check-in da Vistoria</span>
              </div>
              <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                {new Date(checkinData.timestamp).toLocaleString('pt-BR')}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ fontSize: 12, color: '#595959' }}>
                Distância: <strong style={{ color: checkinData.isWithinAllowedRadius ? '#52c41a' : '#faad14' }}>
                  {checkinData.distanceFromPropertyMeters?.toFixed(0) || '?'} m
                </strong>
                <span style={{ color: '#8c8c8c' }}> / {checkinData.allowedRadiusMeters} m permitidos</span>
              </span>
              {checkinData.isWithinAllowedRadius ? (
                <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0 }}>
                  Dentro do raio
                </Tag>
              ) : (
                <Tag color="warning" icon={<WarningOutlined />} style={{ margin: 0 }}>
                  Fora do raio
                </Tag>
              )}
            </div>
          </div>
          {checkinData.latitude !== undefined && checkinData.latitude !== null && checkinData.longitude !== undefined && checkinData.longitude !== null && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#595959' }}>
              Coordenadas: <strong style={{ color: '#262626' }}>
                Lat: {checkinData.latitude.toFixed(6)}, Lng: {checkinData.longitude.toFixed(6)}
              </strong>
            </div>
          )}
          {!checkinData.isWithinAllowedRadius && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c6d1f' }}>
              <WarningOutlined style={{ marginRight: 6 }} />
              O inspetor realizou o check-in fora do raio permitido e optou por continuar com a vistoria.
            </div>
          )}
        </div>
      )}

      {/* Loading state for checkin */}
      {isLoadingCheckin && (
        <div style={{
          marginBottom: 16,
          padding: '10px 16px',
          border: '1px solid #e8e8e8',
          borderRadius: 6,
          backgroundColor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <Spin size="small" />
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>Carregando dados do check-in...</span>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
          Dados do Imóvel
        </h4>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 24 }}>
        {/* Coluna Esquerda - Formulário */}
        <div>
          {/* Seção: Dados do Imóvel */}
          <div style={{ marginBottom: 24, border: '1px solid #e8e8e8', borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h5 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
                Dados do Imóvel
              </h5>
              {/* Botão de editar só aparece se não está em review mode E (não está em correção OU há correção nesta seção) */}
              {!isReviewMode && (!isCorrectionMode || hasPropertyDataCorrection) && (
                <div style={{ display: 'flex', gap: 8 }}>
                  {!isEditingPropertyData ? (
                    <Button
                      type="default"
                      icon={<EditOutlined />}
                      onClick={() => setIsEditingPropertyData(true)}
                    >
                      Editar
                    </Button>
                  ) : (
                    <>
                      <Button
                        icon={<CloseOutlined />}
                        onClick={handleCancelPropertyData}
                        disabled={isSavingPropertyData}
                      >
                        Cancelar
                      </Button>
                      <Button
                        buttonColor="primary"
                        icon={<SaveOutlined />}
                        loading={isSavingPropertyData}
                        onClick={handleSavePropertyData}
                        disabled={!hasPropertyDataChanges}
                      >
                        Salvar
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Tipo de Imóvel */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                Tipo de Imóvel
              </label>
              <ReviewFieldWrapper
                fieldPath="identificacao.tipoImovel"
                fieldLabel="Tipo de Imóvel"
                stepName="Identificação"
                allowEditAllInCorrection={isCorrectionMode}
              >
                <Select
                  value={tipoImovel || undefined}
                  onChange={(value: string) => setTipoImovel(value)}
                  options={tipoImovelOptions}
                  placeholder="Selecione o tipo de imóvel"
                  disabled={!isEditingPropertyData}
                  style={{ width: '100%' }}
                />
              </ReviewFieldWrapper>
            </div>

            {/* Nº Matrícula, Nº IPTU, Nº Cartório */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Nº Matrícula
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.matriculaNumero"
                  fieldLabel="Número da Matrícula"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                    placeholder="Digite o número da matrícula"
                    readOnly={!isEditingPropertyData}
                    style={!isEditingPropertyData ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Nº IPTU
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.iptuNumero"
                  fieldLabel="Número do IPTU"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    value={iptuNumber}
                    onChange={(e) => setIptuNumber(e.target.value)}
                    placeholder="Digite o número do IPTU"
                    readOnly={!isEditingPropertyData}
                    style={!isEditingPropertyData ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Nº Cartório
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.cartorioNumero"
                  fieldLabel="Número do Cartório"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    value={registryOfficeNumber}
                    onChange={(e) => setRegistryOfficeNumber(e.target.value)}
                    placeholder="Digite o número do cartório"
                    readOnly={!isEditingPropertyData}
                    style={!isEditingPropertyData ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
            </div>

            {/* Áreas */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Área de Terreno (m²)
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.areaTotal"
                  fieldLabel="Área de Terreno (m²)"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    type="number"
                    value={landArea}
                    onChange={(e) => setLandArea(e.target.value)}
                    placeholder="Ex: 250.00"
                    step="0.01"
                    min="0"
                    readOnly={!isEditingPropertyData}
                    style={!isEditingPropertyData ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Área Construída (m²)
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.areaConstruida"
                  fieldLabel="Área Construída (m²)"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    type="number"
                    value={builtArea}
                    onChange={(e) => setBuiltArea(e.target.value)}
                    placeholder="Ex: 150.00"
                    step="0.01"
                    min="0"
                    readOnly={!isEditingPropertyData}
                    style={!isEditingPropertyData ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Área Privativa (m²)
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.areaPrivativa"
                  fieldLabel="Área Privativa (m²)"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    type="number"
                    value={privateArea}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPrivateArea(value);
                      // Em editAutomatic propaga no estado do wizard em tempo real
                      // (caso o usuário não clique em "Salvar dados do imóvel" antes
                      // de avançar). A Área Construída usada nos cálculos é editada
                      // no step Avaliação (avaliacao.dadosImovel.areaConstruida).
                      if (mode === 'editAutomatic') {
                        onUpdate({ areaPrivativa: value ? parseFloat(value) : 0 });
                      }
                    }}
                    placeholder="Ex: 120.00"
                    step="0.01"
                    min="0"
                    readOnly={!isEditingPropertyData}
                    style={!isEditingPropertyData ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
            </div>
          </div>

          {/* Seção: Localização do Imóvel */}
          <div style={{ marginBottom: 24, border: '1px solid #e8e8e8', borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h5 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
                Localização do Imóvel
              </h5>
              {/* Botão de editar só aparece se não está em review mode E (não está em correção OU há correção nesta seção) */}
              {!isReviewMode && (!isCorrectionMode || hasAddressCorrection) && (
                <div style={{ display: 'flex', gap: 8 }}>
                  {!isEditingAddress ? (
                    <Button
                      type="default"
                      icon={<EditOutlined />}
                      onClick={() => setIsEditingAddress(true)}
                    >
                      Editar
                    </Button>
                  ) : (
                    <>
                      <Button
                        icon={<CloseOutlined />}
                        onClick={handleCancelAddress}
                        disabled={isSavingAddress}
                      >
                        Cancelar
                      </Button>
                      <Button
                        buttonColor="primary"
                        icon={<SaveOutlined />}
                        loading={isSavingAddress}
                        onClick={handleSaveAddress}
                        disabled={!hasAddressChanges}
                      >
                        Salvar
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* CEP */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                CEP
              </label>
              <ReviewFieldWrapper
                fieldPath="identificacao.enderecoCompleto.cep"
                fieldLabel="CEP"
                stepName="Identificação"
                allowEditAllInCorrection={isCorrectionMode}
              >
                <Spin spinning={isLoadingCep}>
                  <Input
                    value={cep}
                    onChange={handleCepChange}
                    placeholder="Digite o CEP"
                    readOnly={!isEditingAddress}
                    style={!isEditingAddress ? { backgroundColor: '#fafafa' } : {}}
                    suffix={isEditingAddress && <SearchOutlined style={{ color: '#1890ff' }} />}
                  />
                </Spin>
              </ReviewFieldWrapper>
            </div>

            {/* Logradouro e Número */}
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Logradouro
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.enderecoCompleto.logradouro"
                  fieldLabel="Logradouro"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    placeholder="Digite o logradouro"
                    readOnly={!isEditingAddress}
                    style={!isEditingAddress ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Número
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.enderecoCompleto.numero"
                  fieldLabel="Número"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    placeholder="Nº"
                    readOnly={!isEditingAddress}
                    style={!isEditingAddress ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
            </div>

            {/* Complemento */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                Complemento
              </label>
              <ReviewFieldWrapper
                fieldPath="identificacao.enderecoCompleto.complemento"
                fieldLabel="Complemento"
                stepName="Identificação"
                allowEditAllInCorrection={isCorrectionMode}
              >
                <Input
                  value={complement}
                  onChange={(e) => setComplement(e.target.value)}
                  placeholder="Apartamento, bloco, etc."
                  readOnly={!isEditingAddress}
                  style={!isEditingAddress ? { backgroundColor: '#fafafa' } : {}}
                />
              </ReviewFieldWrapper>
            </div>

            {/* Bairro */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                Bairro
              </label>
              <ReviewFieldWrapper
                fieldPath="identificacao.enderecoCompleto.bairro"
                fieldLabel="Bairro"
                stepName="Identificação"
                allowEditAllInCorrection={isCorrectionMode}
              >
                <Input
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  placeholder="Digite o bairro"
                  readOnly={!isEditingAddress}
                  style={!isEditingAddress ? { backgroundColor: '#fafafa' } : {}}
                />
              </ReviewFieldWrapper>
            </div>

            {/* Município e Estado */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Município
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.enderecoCompleto.cidade"
                  fieldLabel="Município"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Digite o município"
                    readOnly={!isEditingAddress}
                    style={!isEditingAddress ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
                  Estado
                </label>
                <ReviewFieldWrapper
                  fieldPath="identificacao.enderecoCompleto.estado"
                  fieldLabel="Estado"
                  stepName="Identificação"
                  allowEditAllInCorrection={isCorrectionMode}
                >
                  <Input
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="UF"
                    maxLength={2}
                    readOnly={!isEditingAddress}
                    style={!isEditingAddress ? { backgroundColor: '#fafafa' } : {}}
                  />
                </ReviewFieldWrapper>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div
            style={{
              height: 1,
              backgroundColor: '#e8e8e8',
              margin: '24px 0',
            }}
          />

          {/* Dados do Proponente (Read-only sempre) */}
          <h5 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
            Dados do{inspection.proponents && inspection.proponents.length > 1 ? 's' : ''} Proponente{inspection.proponents && inspection.proponents.length > 1 ? 's' : ''}
            {inspection.proponents && inspection.proponents.length > 1 && (
              <span style={{ fontSize: 12, fontWeight: 400, color: '#999', marginLeft: 8 }}>
                ({inspection.proponents.length} proponentes)
              </span>
            )}
          </h5>

          {inspection.proponents && inspection.proponents.length > 0 ? (
            <div
              style={{
                maxHeight: 400,
                overflowY: 'auto',
                paddingRight: 8,
              }}
            >
              {inspection.proponents.map((proponent, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: 12,
                    padding: 12,
                    border: '1px solid #e8e8e8',
                    borderRadius: 6,
                    backgroundColor: '#fafafa'
                  }}
                >
                  {inspection.proponents.length > 1 && (
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#1890ff' }}>
                      Proponente {index + 1}
                    </div>
                  )}

                  {/* Layout compacto: 2 colunas */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                    <div>
                      <div style={{ color: '#8c8c8c', marginBottom: 4 }}>Nome</div>
                      <div style={{ fontWeight: 500, color: '#262626' }}>{proponent.name || '-'}</div>
                    </div>
                    <div>
                      <div style={{ color: '#8c8c8c', marginBottom: 4 }}>CPF/CNPJ</div>
                      <div style={{ fontWeight: 500, color: '#262626' }}>{proponent.cpfCnpj || '-'}</div>
                    </div>
                    <div>
                      <div style={{ color: '#8c8c8c', marginBottom: 4 }}>E-mail</div>
                      <div style={{ fontWeight: 500, color: '#262626', wordBreak: 'break-all' }}>
                        {proponent.email || '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#8c8c8c', marginBottom: 4 }}>
                        Telefone{proponent.phones && proponent.phones.length > 1 ? 's' : ''}
                      </div>
                      {proponent.phones && proponent.phones.length > 0 ? (
                        <div>
                          {proponent.phones.map((phone, phoneIndex) => (
                            <div key={phoneIndex} style={{ fontWeight: 500, color: '#262626', marginBottom: 2 }}>
                              {phone.phoneNumber} {phone.phoneType && `(${phone.phoneType})`}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontWeight: 500, color: '#262626' }}>-</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>
              Nenhum proponente cadastrado
            </div>
          )}
        </div>

        {/* Coluna Direita - Mapa */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 13,
              marginBottom: 8,
              color: '#595959',
              fontWeight: 500,
            }}
          >
            Localização no Mapa
          </label>
          <div
            style={{
              height: 520,
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #d9d9d9',
              backgroundColor: '#f5f5f5',
            }}
          >
            <iframe
              key={mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src={mapUrl}
              title="Mapa da localização do imóvel"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
